import { useCallback, useState } from 'react'
import { QueryKey, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

type UsePollingQueryProps<T> = {
  queryKey: QueryKey
  queryFn: () => Promise<T>
  intervalMs: number
  startOnLoad?: boolean
}

const usePollingQuery = <T>({
  queryKey,
  queryFn,
  intervalMs,
  startOnLoad = false,
}: UsePollingQueryProps<T>) => {
  const [isPolling, setIsPolling] = useState(startOnLoad)

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: isPolling,
    retry: 0,
    refetchInterval: isPolling ? intervalMs : false,
  })

  const startRefresh = useCallback(async () => {
    setIsPolling(true)
    await query.refetch()
  }, [query])

  const stopRefresh = useCallback(() => {
    setIsPolling(false)
  }, [])

  const resetRefresh = useCallback(() => {
    setIsPolling(true)
  }, [])

  const loadedTime = query.dataUpdatedAt
    ? dayjs(query.dataUpdatedAt).format('HH:mm:ss')
    : ''

  return {
    data: query.data,
    isLoading: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    startRefresh,
    stopRefresh,
    resetRefresh,
    loadedTime,
  }
}

export default usePollingQuery
